import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, firestore} from "../../firebase/firebase.js";
import { onAuthStateChanged, getIdTokenResult, createUserWithEmailAndPassword, verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup, GoogleAuthProvider, signOut } from "firebase/auth";
import { doc, setDoc, deleteDoc, addDoc, getDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot, writeBatch, collection, query, where, getDocs, limit } from "firebase/firestore";
import useShowToast from "../../hooks/useShowToast";
import { requestNotificationPermission } from "../../utils/fcmNotifications/fcmNotifications";
import NotificationStack from '../NotificationStack/NotificationStack';

// Create Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const showToast = useShowToast();
  const [messages, setMessages] = useState([]);
  const [claims, setClaims] = useState(null);
  
  const notify = (type, content) => {
    var id = 0;
	setTimeout(() => {
	  id = Date.now();
      setMessages(prev => [...prev, { id, type, content }]);
    }, 10);
    

    setTimeout(() => {
      setMessages(prev => prev.filter(m => m.id !== id));
    }, 4000);
  };


  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
     setUser(currentUser);
	  if (currentUser) {
		const token = await getIdTokenResult(currentUser, true);
		//const tokenResult = await currentUser.getIdTokenResult();
		//console.log(tokenResult)
		setClaims(token.claims);
		//await 
		saveFCMToken(currentUser.uid);
		await fetchUserData(currentUser.uid);
      } else {
        setUserData(null);
		setClaims(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
	if(user == null | user == undefined) return;
		const unsub = onSnapshot(doc(firestore, "users", user.uid), async(doc) => {
		 await setUserData(doc.data());
	});

    return () => unsub();
  }, [user]);

  // Fetch user data from Firestore
  const fetchUserData = async (uid) => {
	const userRef = await doc(firestore, "users", uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      await setUserData(userSnap.data());
    }
  };
  
	// Store FCM Token in Firestore
  const saveFCMToken = async (userId) => {
	  const fcmToken = await requestNotificationPermission();
	  if (fcmToken) {
		await updateDoc(doc(firestore, "users", userId), { fcmToken });
	  }
   };
   
  const getRole = async () => {
	  return claims?.role;
   };
  
  const refreshData = async () => {
	if(user?.uid) {
		const userRef = await doc(firestore, "users", user?.uid);
		const userSnap = await getDoc(userRef);

		if (userSnap.exists()) {
		  await setUserData(userSnap.data());
		}
	}
  };

  
  // Search for users by name or email
  const searchUsersSize = async (search, limitval = 10) => {
	const usersRef = collection(firestore, 'users');
	const q = query(usersRef, where('username', '>=', search), where('username', '<=', search + '\uf8ff'), limit(limitval));
	const snapshot = await getDocs(q);
	const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(user_ => user_.id !==  user.uid);
    return users;
  };
  
  // Search for users by name or email
  const searchUsers = async (searchTerm, limitval = 10) => {
    var fuser = [];
	const usersRef = collection(firestore, "users");
    const q = query(usersRef, where("username", ">=", searchTerm), where('username', '<=', searchTerm + '\uf8ff'), limit(limitval));
    const querySnapshot = await getDocs(q);
    const qe = query(usersRef, where("email", ">=", searchTerm), where('email', '<=', searchTerm + '\uf8ff'), limit(limitval));
    const querySnapshote = await getDocs(qe);

    const usersfromusername = querySnapshot.docs.map(doc => (
	{
      id: doc.id,
      ...doc.data()
    }));
	
    const usersfromemail = querySnapshote.docs.map(doc => (
	{
      id: doc.id,
      ...doc.data()
    }));

	for (const doc of usersfromusername) {
		if(doc.id != user.uid)
			fuser.push(doc);
	}
	
	for (const doc of usersfromemail) {
		if(doc.id != user.uid)
			fuser.push(doc);
	}
	
	const unique = fuser.filter((obj, index) => fuser.findIndex((item) => item.id === obj.id) === index);

    return unique;
  };

  const searchbyUsersId = async (searchTerm) => {
    var user = [];
	
	const usersRef = collection(firestore, "users");
    const q = query(usersRef, where("uid", "==", searchTerm));
    const querySnapshot = await getDocs(q);
    const usersid = querySnapshot.docs.map(doc => (
	{
      id: doc.id,
      ...doc.data()
    }));

	for (const doc of usersid) {
		user.push(doc);
	}
	
	
	const unique = user.filter((obj, index) => user.findIndex((item) => item.id === obj.id) === index);

    return unique;
  };


  const handleFollow = async (targetUserId) => {
    if (user && targetUserId && user.uid != targetUserId) {
		try {
			const userRef = doc(firestore, "users", user.uid);
			const targetUserRef = doc(firestore, "users", targetUserId);

			if(targetUserRef != null) {
			  await updateDoc(userRef, {
				following: arrayUnion(targetUserId),
			  });

			  await updateDoc(targetUserRef, {
				followers: arrayUnion(user.uid),
			  });
			}
			await fetchUserData(user.uid); // Refresh user data
			notify("info","Great!");
		}
		catch(error) {
		}
    }
  };

  const isFollowing = async (targetUserId) => {
    if (user && targetUserId && user.uid != targetUserId) {
		try {
			const userRef = doc(firestore, "users", user.uid);
			const userSnap = await getDoc(userRef);
        
			if (userSnap.exists() && userSnap.data().following?.includes(targetUserId)) {
			  return true;
			}
		}
		catch(error) {
		}
    }
	return false;
  };

  const handleUnfollow = async (targetUserId) => {
    if (user && targetUserId && user.uid != targetUserId) {
      try {
		  const userRef = doc(firestore, "users", user.uid);
		  const targetUserRef = doc(firestore, "users", targetUserId);

		  if(targetUserRef != null) {
			  await updateDoc(userRef, {
				following: arrayRemove(targetUserId),
			  });

			  await updateDoc(targetUserRef, {
				followers: arrayRemove(user.uid),
			  });
		   }
		   await fetchUserData(user.uid); // Refresh user data
		   notify("info","Great!");
	   }
	   catch(error)
	   {
	   }
    }
  };

  //delete a friend
  const removeFriendRequest = async (requesterId) => {
	if (!user || user.uid == requesterId) return;

    const userRef = doc(firestore, "users", user.uid);
    const requesterRef = doc(firestore, "users", requesterId);

    // Remove request from friendRequests
    await updateDoc(userRef, {
      friends: arrayRemove(requesterId)
    });

    // Add current user to the requester's friends list
    await updateDoc(requesterRef, {
      friends: arrayRemove(user.uid)
    });

    notify("info","Friend deleted!");
    await fetchUserData(user.uid); // Refresh user data
  };
  
  const declineFriendRequest = async (requesterId) => {

	if (!user || user.uid == requesterId) return;

    const userRef = doc(firestore, "users", user.uid);
    const requesterRef = doc(firestore, "users", requesterId);

    // Remove request from friendRequests
    await updateDoc(userRef, {
      friendRequests: arrayRemove(requesterId),
    });

    notify("info","Friend request removed!");
    await fetchUserData(user.uid); // Refresh user data
  };

  // Send a Friend Request
  const sendFriendRequest = async (receiverId) => {
    if (!user || user.uid == receiverId) return;

    const senderRef = doc(firestore, "users", user.uid);
    const receiverRef = doc(firestore, "users", receiverId);

    const receiverSnap = await getDoc(receiverRef);
    if (!receiverSnap.exists()) return;

    const receiverData = receiverSnap.data();
	//console.log(user.uid, receiverData)
	
    if (receiverData.friendRequests?.includes(user.uid)) {
      notify("info","Friend request already sent!");
      return;
    }

    await updateDoc(receiverRef, {
      friendRequests: arrayUnion(user.uid)
    });

    notify("info","Friend request sent!");
  };
 
 
  const acceptFriendRequest = async (requesterId) => {
    if (!user || user.uid == requesterId) return;

    const userRef = doc(firestore, "users", user.uid);
    const requesterRef = doc(firestore, "users", requesterId);

    // Remove request from friendRequests
    await updateDoc(userRef, {
      friendRequests: arrayRemove(requesterId),
      friends: arrayUnion(requesterId)
    });

    // Add current user to the requester's friends list
    await updateDoc(requesterRef, {
      friendRequests: arrayRemove(user.uid),
	  friends: arrayUnion(user.uid)
    });

    notify("info","Friend request accepted!");
    await fetchUserData(user.uid); // Refresh user data
  };
  
  
  const sendNotification = async (toUserId, fromUser, type, debateId = null) => {
  if (!toUserId || !fromUser) return;

  const toUserRef = doc(firestore, "users", toUserId);
  const toUserSnap = await getDoc(toUserRef);

  if (!toUserSnap.exists()) return;

  const toUserData = toUserSnap.data();
  let message = "";

  if (type === "follow") {
    message = `${fromUser.username} has followed you.`;
  } else if (type === "debate_invite") {
    message = `${fromUser.username} invited you to a debate.`;
  } else if (type === "debate_challenge") {
    message = `${fromUser.username} challenged you to a debate!`;
  }

  // Store notification in Firestore
  await addDoc(collection(firestore, "users", toUserData.uid, "actions"), {
    toUserId,
    fromUserId: fromUser.uid,
    fromUserName: fromUser.displayName,
    fromUserPhoto: fromUser.photoURL,
    type,
    debateId,
    message,
    timestamp: new Date().toISOString(),
    read: false
  });
};
  
  const markNotificationAsRead = async (userId, notificationId) => {  
	  const notifRef = doc(firestore, `users/${userId}/notifications/${notificationId}`);
	  await updateDoc(notifRef, { read: true });
	}

  const deleteNotification = async (userId, notificationId) => {  
	  const notifRef = doc(firestore, `users/${userId}/notifications/${notificationId}`);
	  await deleteDoc(notifRef);
	}

  const markAllNotificationsAsRead = async (userId) => {  
	  const q = query(collection(firestore, `users/${userId}/notifications`));
	  const snapshot = await getDocs(q);
	  const batch = writeBatch(firestore);
	  snapshot.forEach(docSnap => {
		batch.update(docSnap.ref, { read: true });
	  });
	  await batch.commit();
	}

  

  // Signup function
  const signup = async (username, email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(firestore, "users", user.uid);
      const userSnap = await getDoc(userRef);
      const createdDate  = new Date().toISOString();
      // Store user info in Firestore
      if (!userSnap.exists()) {
        await setDoc(userRef, {
        uid: user.uid,
        username: username,
        email: user.email,
        photoURL: user.photoURL,
        createdAt: createdDate,
        eloRanking: 1000, // Default starting rank
        totalDebates: 0,
        wins: 0,
        losses: 0,
        followers: [],
        following: [],
        friendRequests: [],
        friends: [],
        });
      }

      setUserData({
        uid: user.uid,
        username: username,
        email: user.email,
        photoURL: user.photoURL,
        createdAt: createdDate,
        eloRanking: 1000, // Default starting rank
        totalDebates: 0,
        wins: 0,
        losses: 0,
        followers: [],
        following: [],
        friendRequests: [],
        friends: [],
      });
      await saveFCMToken(user.uid);
    } catch(error) {
      throw error;
    }
  };

  
  const confirmPasswordResetNow = async (actionCode, newPassword) => {
      try {
        const resp = await confirmPasswordReset(auth, actionCode, newPassword);
        return resp;
	   }
	   catch(error)
	   {
      return error;
	   }
   return "";
  };

  const verifyPasswordReset = async (oobcode) => {
      try {
        const email = await verifyPasswordResetCode(auth, oobcode);
        return email;
	   }
	   catch(error)
	   {
      return "";
	   }
   return "";
  };

  // Login function
  const passwordReset = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };


  // Login function
  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    await fetchUserData(userCredential.user.uid);
	await saveFCMToken(userCredential.user.uid);
  };

  const loginWithGoogle = async () => {
    const createdDate  = new Date().toISOString();
	const provider = new GoogleAuthProvider();
    try {
       const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(firestore, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          username: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
          createdAt: createdDate,
          eloRanking: 1000,
          totalDebates: 0,
          wins: 0,
          losses: 0,
			followers: [],
			following: [],
			friendRequests: [],
			friends: [],
        });
		
		setUserData({
			uid: user.uid,
			username: user.displayName,
			email: user.email,
			photoURL: user.photoURL,
			createdAt: createdDate,
			eloRanking: 1000, // Default starting rank
			totalDebates: 0,
			wins: 0,
			losses: 0,
			followers: [],
			following: [],
			friendRequests: [],
			friends: [],
		});
      } else {
		await fetchUserData(user.uid);
	  }
	  
    } catch (error) {
      notify("error", error.message);
    }
  };
  
  // Logout function
  const logout = async () => {
    await signOut(auth);
    setUserData(null);
  };

  const sendRatioCoin = async(receiverId, amount) => {
	  const userRef = doc(firestore, "users", user.uid);

	  await updateDoc(userRef, {
		pendingTransaction: {
		  receiverId,
		  amount,
		  processed: false,
		  createdAt: new Date().toISOString(),
		},
	  });
  };

  const listenToWallet = (callback) => {
	  const userRef = doc(firestore, "users", user.uid);
	  //console.log(userRef);
	  return onSnapshot(userRef, (snap) => callback(snap.data()));
  }

  const listenToTransactions = (callback) => {
	  const ref = collection(firestore, "users", user.uid, "transactions");
	  	  //console.log(ref);

	  return onSnapshot(ref, (snap) =>
		callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
	  );
  }


  return (
    <AuthContext.Provider value={{sendRatioCoin, listenToWallet, listenToTransactions, getRole, claims, searchUsersSize, isFollowing, declineFriendRequest, handleUnfollow, handleFollow, markAllNotificationsAsRead, markNotificationAsRead, deleteNotification, sendNotification, refreshData, user, userData, loading, signup, login, passwordReset, verifyPasswordReset, confirmPasswordResetNow, loginWithGoogle, searchbyUsersId, acceptFriendRequest, removeFriendRequest, sendFriendRequest, searchUsers, logout }}>
      {!loading && children}
	  <NotificationStack messages={messages} />
    </AuthContext.Provider>
  );
};

// Custom hook to use AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};
